'use client'

import { AnimatePresence, motion } from 'motion/react'
import { usePathname, useRouter } from 'next/navigation'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FaqSection, IFaqSectionProps } from '@/features/faq/faq-section'
import { hashToTitle, titleToHash } from '@/utils/faq-hash'

interface IFaqProps {
    sections: IFaqSectionProps[]
    withTitles?: boolean
}

export const FaqComponent = ({ sections, withTitles = true }: IFaqProps) => {
    const router = useRouter()
    const pathname = usePathname()
    const [selectedSectionTitle, setSelectedSectionTitle] = useState<string | null>(null)

    useEffect(() => {
        const initializeFromHash = () => {
            if (!pathname.includes('/faq')) {
                if (sections.length > 0) {
                    setSelectedSectionTitle(sections[0].title)
                }
                return
            }

            const hash = window.location.hash
            const sectionTitles = sections.map((s) => s.title)

            if (hash && sections.length > 0) {
                const matchedTitle = hashToTitle(hash, sectionTitles)
                if (matchedTitle) {
                    setSelectedSectionTitle(matchedTitle)
                    return
                }
            }

            if (sections.length > 0) {
                const firstSection = sections[0].title
                setSelectedSectionTitle(firstSection)
                const newHash = titleToHash(firstSection)
                router.replace(`${pathname}#${newHash}`, { scroll: false })
            }
        }

        initializeFromHash()
    }, [sections, router, pathname])

    useEffect(() => {
        const handleHashChange = () => {
            if (!pathname.includes('/faq')) {
                return
            }

            const hash = window.location.hash
            const sectionTitles = sections.map((s) => s.title)
            const matchedTitle = hashToTitle(hash, sectionTitles)

            if (matchedTitle) {
                setSelectedSectionTitle(matchedTitle)
            }
        }

        window.addEventListener('hashchange', handleHashChange)
        return () => window.removeEventListener('hashchange', handleHashChange)
    }, [sections, pathname])

    const createOnSectionClick = useCallback(
        (title: string) => () => {
            const newSelectedTitle = selectedSectionTitle === title ? null : title
            setSelectedSectionTitle(newSelectedTitle)

            // Only manipulate URL hash if we're on the main FAQ page
            if (!pathname.includes('/faq')) {
                // For non-FAQ pages, just handle the selection without URL changes
                if (!newSelectedTitle && sections.length > 0) {
                    setSelectedSectionTitle(sections[0].title)
                }
                return
            }

            if (newSelectedTitle) {
                const newHash = titleToHash(newSelectedTitle)
                router.replace(`${pathname}#${newHash}`, { scroll: false })
            } else {
                const firstSection = sections[0]?.title
                if (firstSection) {
                    setSelectedSectionTitle(firstSection)
                    const newHash = titleToHash(firstSection)
                    router.replace(`${pathname}#${newHash}`, { scroll: false })
                }
            }
        },
        [selectedSectionTitle, router, pathname, sections]
    )

    const selectedSection = useMemo(
        () => sections.find((section) => section.title === selectedSectionTitle),
        [sections, selectedSectionTitle]
    )

    return (
        <div className="flex flex-col md:items-center gap-6 select-none md:gap-8 w-full md:max-w-full max-w-[100vw] overflow-hidden">
            {withTitles && (
                <div className="flex flex-row gap-2 md:gap-4 overflow-x-auto px-4 scrollbar-hide relative md:min-w-fit">
                    {sections.map((section) => {
                        return (
                            <button
                                onClick={createOnSectionClick(section.title)}
                                key={'faq' + 'section-title' + section.title}
                                className={`webkit-tap-transparent shrink-0 w-fit rounded-[40px] min-h-8 flex items-center cursor-pointer ${selectedSectionTitle === section.title ? 'bg-[#11301D] text-white' : 'text-black'}`}
                            >
                                <p className=" font-semibold text-sm p-3">{section.title}</p>
                            </button>
                        )
                    })}
                </div>
            )}

            <div className="flex flex-col gap-3 w-full px-4 md:px-0">
                <AnimatePresence mode="wait" initial={false}>
                    {selectedSection && (
                        <motion.div
                            key={'faq-section' + selectedSection.title + 'selected'}
                            initial={{ opacity: 0, filter: 'brightness(0.9)' }}
                            animate={{ opacity: 1, filter: 'brightness(1)' }}
                            exit={{ opacity: 0, filter: 'brightness(0.9)' }}
                            transition={{ duration: 0.35, ease: 'easeOut' }}
                        >
                            <FaqSection {...selectedSection} />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    )
}
